import React from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "../interact/Button";
import { detectPartnerFromHost } from "../../partner/detect";
import { SUPPORT_EMAIL } from "../../utils/support";

/**
 * ErrorBoundary
 * -------------
 * Catches a crash while rendering, so one bad response or bug shows a message instead of
 * blanking the whole app (React unmounts the entire tree on an uncaught render error).
 *
 * Used twice:
 * - `fullPage` around the whole app (app/App.tsx) — the last line of defence, also covers
 *   the login and reset pages.
 * - inside the layout around the page outlet, with `resetKey` set to the path — the sidebar
 *   stays usable and navigating to another page clears the error.
 *
 * The fallback is deliberately self-contained, like the holding page: no router, no API,
 * no stores — only host-based partner branding — so it cannot itself fail the same way.
 * Errors still reach the console; this app has no client-side telemetry.
 */

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Fill the screen (app root) instead of the content area (inside the layout). */
  fullPage?: boolean;
  /** When this changes, a caught error is cleared and the children render again. */
  resetKey?: unknown;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("[ErrorBoundary] render crash", error, info.componentStack);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  private handleRetry = () => this.setState({ hasError: false });

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <ErrorFallback
        fullPage={this.props.fullPage}
        onRetry={this.handleRetry}
      />
    );
  }
}

const ErrorFallback: React.FC<{ fullPage?: boolean; onRetry: () => void }> = ({
  fullPage,
  onRetry,
}) => {
  const partnerPreset = detectPartnerFromHost();

  const card = (
    <div
      role="alert"
      className="w-full max-w-xl bg-card border border-border rounded-2xl shadow-sm p-8 sm:p-10 flex flex-col items-center text-center"
    >
      {fullPage && (
        <img
          src={partnerPreset.logoHref}
          alt={partnerPreset.title}
          className="w-auto h-8 object-contain mb-8"
        />
      )}

      <div className="w-14 h-14 flex items-center justify-center rounded-xl mb-6 bg-dangerLight text-danger">
        <AlertTriangle className="w-7 h-7" aria-hidden="true" />
      </div>

      <h1 className="text-2xl font-bold text-foreground mb-3">Something went wrong</h1>

      <p className="text-sm text-muted-foreground mb-6">
        This page ran into a problem and couldn't be shown. Your data is safe — try again,
        or reload the page.
      </p>

      <div className="flex gap-3">
        {!fullPage && (
          <Button variant="secondary" onClick={onRetry}>
            Try again
          </Button>
        )}
        <Button onClick={() => window.location.reload()}>Reload page</Button>
      </div>

      <p className="text-sm text-muted-foreground mt-8">
        If it keeps happening, write to{" "}
        <a
          href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent("Distributor Dashboard")}`}
          className="font-semibold text-vLinkColor underline underline-offset-2 hover:text-vLinkHoverColor focus:text-vLinkHoverColor focus:outline-none focus:underline"
        >
          {SUPPORT_EMAIL}
        </a>
      </p>
    </div>
  );

  if (!fullPage) {
    return <div className="flex justify-center py-12">{card}</div>;
  }

  return (
    <main className="min-h-screen flex items-center justify-center bg-partnerBg px-4 py-12">
      {card}
    </main>
  );
};
